import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ArrowLeft, Edit, User, Calendar, Clock, AlertTriangle, Link, Monitor, FileText, Download, MessageSquare, Activity, Send } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api` || 'http://localhost:8001/api';

// Helper function to get current user information
const getCurrentUser = () => {
  try {
    const userData = localStorage.getItem('bizmanager_user');
    if (userData) {
      const user = JSON.parse(userData);
      
      if (user.isAdmin) {
        return {
          type: 'Admin',
          name: user.name || user.email || 'Admin User',
          id: user.id
        };
      } else if (user.isEmployee) {
        return {
          type: 'Employee',
          name: user.name || 'Employee User',
          id: user.id
        };
      } else {
        // Client user
        return {
          type: 'Client',
          name: user.name || user.email || 'Client User',
          id: user.id
        };
      }
    }
  } catch (e) {
    console.error('Error parsing user data:', e);
  }
  
  // Default fallback
  return {
    type: 'User',
    name: 'Current User',
    id: 'unknown'
  };
};

// Helper function to get proper user display name for activity logs
const getUserDisplayName = () => {
  try {
    const userData = localStorage.getItem('bizmanager_user');
    if (userData) {
      const user = JSON.parse(userData);
      
      if (user.isAdmin) {
        return 'Admin';
      } else if (user.isEmployee) {
        return user.name || 'Employee';
      } else {
        // Client user - could be a client contact
        return user.name || user.email || 'Client';
      }
    }
  } catch (e) {
    console.error('Error parsing user data:', e);
  }
  
  return 'System';
};

const SupportTicketDetailEnhanced = () => {
  const navigate = useNavigate();
  const { ticketId } = useParams();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [teams, setTeams] = useState([]);
  const [activeTab, setActiveTab] = useState('details');
  
  // Notes functionality
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  
  // Logs functionality
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    fetchTicket();
    fetchEmployees();
    fetchTeams();
    fetchNotes();
    fetchLogs();
  }, [ticketId]);

  const fetchTicket = async () => {
    try {
      const response = await axios.get(`${API}/support-tickets/${ticketId}`);
      setTicket(response.data);
    } catch (error) {
      console.error('Error fetching ticket:', error);
      alert('Failed to load ticket details');
      navigate('/support/tickets');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`);
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/team-types`);
      setTeams(response.data);
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  const fetchNotes = async () => {
    try {
      const response = await axios.get(`${API}/support-tickets/${ticketId}/notes`);
      // Sort notes with latest first (same as project view)
      const sortedNotes = (response.data || []).sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setNotes(sortedNotes);
    } catch (error) {
      console.error('Error fetching notes:', error);
      // If endpoint doesn't exist, use empty array
      setNotes([]);
    }
  };

  const fetchLogs = async () => {
    try {
      const response = await axios.get(`${API}/support-tickets/${ticketId}/logs`);
      setLogs(response.data);
    } catch (error) {
      console.error('Error fetching logs:', error);
      // If endpoint doesn't exist, use empty array
      setLogs([]);
    }
  };

  const updateTicketStatus = async (newStatus) => {
    setUpdating(true);
    try {
      const response = await axios.put(`${API}/support-tickets/${ticketId}`, {
        current_status: newStatus
      });
      setTicket(response.data);
      
      // Log the status change
      await logActivity(`Status changed to ${newStatus}`);
      fetchLogs();
      
      alert('Ticket status updated successfully');
    } catch (error) {
      console.error('Error updating ticket status:', error);
      alert('Failed to update ticket status');
    } finally {
      setUpdating(false);
    }
  };

  const updateTicketTeam = async (teamId) => {
    setUpdating(true);
    try {
      const response = await axios.put(`${API}/support-tickets/${ticketId}`, {
        team_assigned: teamId
      });
      setTicket(response.data);
      
      const team = teams.find(t => t.id === teamId);
      await logActivity(`Team assigned: ${team?.name || 'Unknown'}`);
      fetchLogs();
      
      alert('Ticket team updated successfully');
    } catch (error) {
      console.error('Error updating ticket team:', error);
      alert('Failed to update ticket team');
    } finally {
      setUpdating(false);
    }
  };

  const assignToEmployee = async (employeeId) => {
    setUpdating(true);
    try {
      const response = await axios.get(`${API}/support-tickets/${ticketId}/assign/${employeeId}`);
      setTicket(response.data);
      
      const employee = employees.find(e => e.id === employeeId);
      await logActivity(`Assigned to employee: ${employee?.name || 'Unknown'}`);
      fetchLogs();
      
      alert('Ticket assigned successfully');
    } catch (error) {
      console.error('Error assigning ticket:', error);
      alert('Failed to assign ticket');
    } finally {
      setUpdating(false);
    }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    
    setAddingNote(true);
    try {
      const userData = localStorage.getItem('bizmanager_user');
      const user = userData ? JSON.parse(userData) : null;
      const userDisplayName = getUserDisplayName();
      
      const noteData = {
        content: newNote.trim(),
        created_by: user?.id,
        created_by_name: userDisplayName
      };
      
      await axios.post(`${API}/support-tickets/${ticketId}/notes`, noteData);
      
      // Add to local state for immediate update (latest first, same as project view)
      const noteForState = {
        id: Date.now().toString(),
        content: newNote.trim(),
        created_at: new Date().toISOString(),
        created_by: user?.id,
        created_by_name: userDisplayName
      };
      
      setNotes(prev => [noteForState, ...prev]);  // Latest first
      setNewNote('');
      
      await logActivity(`Added note: ${newNote.substring(0, 50)}${newNote.length > 50 ? '...' : ''}`);
      fetchLogs();
      
    } catch (error) {
      console.error('Error adding note:', error);
      // Still add to local state as fallback (latest first) 
      const userData = localStorage.getItem('bizmanager_user');
      const user = userData ? JSON.parse(userData) : null;
      const userDisplayName = getUserDisplayName();
      
      const noteData = {
        id: Date.now().toString(),
        content: newNote.trim(),
        created_at: new Date().toISOString(),
        created_by: user?.id,
        created_by_name: userDisplayName
      };
      setNotes(prev => [noteData, ...prev]);  // Latest first  
      setNewNote('');
    } finally {
      setAddingNote(false);
    }
  };

  const logActivity = async (activity) => {
    try {
      const userData = localStorage.getItem('bizmanager_user');
      const user = userData ? JSON.parse(userData) : null;
      const userDisplayName = getUserDisplayName();
      
      const logData = {
        activity,
        created_by: user?.id,
        created_by_name: userDisplayName,
        created_at: new Date().toISOString()
      };
      
      await axios.post(`${API}/support-tickets/${ticketId}/logs`, logData);
    } catch (error) {
      console.error('Error logging activity:', error);
      // Add to local logs as fallback
      const userData = localStorage.getItem('bizmanager_user');
      const user = userData ? JSON.parse(userData) : null;
      const userDisplayName = getUserDisplayName();
      
      const logData = {
        id: Date.now().toString(),
        activity,
        created_by: user?.id,
        created_by_name: userDisplayName,
        created_at: new Date().toISOString()
      };
      setLogs(prev => [logData, ...prev]);
    }
  };

  const downloadFile = async (fileUrl, fileName, index) => {
    try {
      // Log the download activity
      await logActivity(`Downloaded file: ${fileName || 'attachment'}`);
      fetchLogs();
      
      // Use the backend download endpoint instead of direct file access
      const downloadUrl = `${API}/support-tickets/${ticketId}/attachments/${index}/download`;
      
      // Create download link
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Failed to download file');
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Open':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'In Progress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'In Testing':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Resolved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Published (Prod)':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'Closed':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityBadgeClass = (priority) => {
    switch (priority) {
      case 'Critical':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'High':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'Medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Low':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="text-gray-500 ml-3">Loading ticket details...</p>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Ticket not found</h2>
          <p className="text-gray-500 mt-2">The requested ticket could not be found.</p>
          <button
            onClick={() => navigate('/support/tickets')}
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Back to Tickets
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/support/tickets')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{ticket.issue_name}</h1>
            <p className="text-gray-600 mt-1">Ticket #{ticket.id.slice(-8)} • {ticket.client_name}</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <span className={`px-3 py-1 text-sm font-medium rounded-full border ${getPriorityBadgeClass(ticket.priority)}`}>
            {ticket.priority} Priority
          </span>
          <span className={`px-3 py-1 text-sm font-medium rounded-full border ${getStatusBadgeClass(ticket.current_status)}`}>
            {ticket.current_status}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'details', name: 'Details', icon: FileText },
            { id: 'notes', name: 'Notes', icon: MessageSquare },
            { id: 'logs', name: 'Activity Logs', icon: Activity }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              <span>{tab.name}</span>
              {tab.id === 'notes' && notes.length > 0 && (
                <span className="bg-blue-100 text-blue-600 text-xs rounded-full px-2 py-1">
                  {notes.length}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2">
          {activeTab === 'details' && (
            <div className="space-y-6">
              {/* Issue Details */}
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Issue Details</h2>
                
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Description</h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-gray-900 whitespace-pre-wrap">{ticket.issue_description}</p>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Steps to Reproduce</h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      {Array.isArray(ticket.issue_steps) ? (
                        <ol className="list-none space-y-2">
                          {ticket.issue_steps.map((step, index) => (
                            <li key={index} className="flex items-start space-x-3">
                              <div className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-medium">
                                {index + 1}
                              </div>
                              <span className="text-gray-900">{step}</span>
                            </li>
                          ))}
                        </ol>
                      ) : (
                        <p className="text-gray-900 whitespace-pre-wrap">{ticket.issue_steps}</p>
                      )}
                    </div>
                  </div>

                  {ticket.issue_link && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-700 mb-2">Related Link</h3>
                      <a
                        href={ticket.issue_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-800"
                      >
                        <Link className="h-4 w-4" />
                        <span>{ticket.issue_link}</span>
                      </a>
                    </div>
                  )}

                  {ticket.issue_device && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-700 mb-2">Device/Platform</h3>
                      <div className="flex items-center space-x-2">
                        <Monitor className="h-4 w-4 text-gray-500" />
                        <span className="text-gray-900">{ticket.issue_device}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Documents */}
              {ticket.documents && ticket.documents.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Attachments</h2>
                  <div className="space-y-3">
                    {ticket.documents.map((doc, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <FileText className="h-5 w-5 text-gray-500" />
                          <span className="text-gray-900">Attachment {index + 1}</span>
                        </div>
                        <button
                          onClick={() => downloadFile(doc, `attachment-${index + 1}`, index)}
                          className="text-blue-600 hover:text-blue-800 p-1 flex items-center space-x-1"
                        >
                          <Download className="h-4 w-4" />
                          <span className="text-sm">Download</span>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'notes' && (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Ticket Notes</h2>
                  <p className="text-gray-600 text-sm">Timeline-based notes and updates</p>
                </div>
                <button
                  onClick={() => setNewNote(newNote || '')}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                >
                  Add Note
                </button>
              </div>
              
              {/* Add Note Section */}
              <div className="mb-6 border-b border-gray-200 pb-6">
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  rows={3}
                  placeholder="Add a note or comment..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
                <div className="flex justify-end mt-2">
                  <button
                    onClick={addNote}
                    disabled={!newNote.trim() || addingNote}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    <Send className="h-4 w-4" />
                    <span>{addingNote ? 'Adding...' : 'Add Note'}</span>
                  </button>
                </div>
              </div>

              {/* Timeline Notes */}
              {notes.length > 0 ? (
                <div className="space-y-4">
                  {notes.map((note, index) => (
                    <div key={note.id || index} className="relative">
                      {/* Timeline Line */}
                      {index < notes.length - 1 && (
                        <div className="absolute left-4 top-12 bottom-0 w-0.5 bg-gray-200"></div>
                      )}
                      
                      {/* Note Card */}
                      <div className="flex space-x-4">
                        {/* Timeline Dot */}
                        <div className="flex-shrink-0 w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </div>
                        
                        {/* Note Content */}
                        <div className="flex-1 bg-white border rounded-lg p-4 shadow-sm">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="text-xs text-gray-500">
                                {format(new Date(note.created_at), "MMMM dd, yyyy 'at' h:mm a")}
                              </p>
                              <p className="text-xs text-blue-600 font-medium">
                                Added by: {note.created_by_name || 'Unknown User'}
                              </p>
                            </div>
                          </div>
                          <p className="text-gray-700 text-sm leading-relaxed">{note.content}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Notes Yet</h3>
                  <p className="text-gray-500 text-sm mb-4">Add ticket notes to track important updates and decisions</p>
                  <button
                    onClick={() => setNewNote('')}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                  >
                    Add First Note
                  </button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'logs' && (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Activity Logs</h2>
              
              <div className="space-y-4">
                {logs.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Activity className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>No activity logs yet.</p>
                  </div>
                ) : (
                  <div className="flow-root">
                    <ul className="-mb-8">
                      {logs.map((log, index) => (
                        <li key={log.id || index}>
                          <div className="relative pb-8">
                            {index !== logs.length - 1 && (
                              <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" />
                            )}
                            <div className="relative flex space-x-3">
                              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-500 ring-8 ring-white">
                                <Activity className="h-4 w-4 text-white" />
                              </div>
                              <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                                <div>
                                  <p className="text-sm text-gray-900">{log.activity}</p>
                                  <p className="text-xs text-gray-500">{log.created_by_name || log.created_by || 'System'}</p>
                                </div>
                                <div className="whitespace-nowrap text-right text-sm text-gray-500">
                                  {new Date(log.created_at).toLocaleString()}
                                </div>
                              </div>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Ticket Info */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Ticket Information</h2>
            
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <User className="h-5 w-5 text-gray-500" />
                <div>
                  <p className="text-sm text-gray-500">Client</p>
                  <p className="font-medium text-gray-900">{ticket.client_name}</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <AlertTriangle className="h-5 w-5 text-gray-500" />
                <div>
                  <p className="text-sm text-gray-500">Issue Type</p>
                  <p className="font-medium text-gray-900">{ticket.issue_type}</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Calendar className="h-5 w-5 text-gray-500" />
                <div>
                  <p className="text-sm text-gray-500">Created</p>
                  <p className="font-medium text-gray-900">
                    {new Date(ticket.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Clock className="h-5 w-5 text-gray-500" />
                <div>
                  <p className="text-sm text-gray-500">Last Updated</p>
                  <p className="font-medium text-gray-900">
                    {new Date(ticket.updated_at).toLocaleDateString()}
                  </p>
                </div>
              </div>

              {ticket.team_name && (
                <div>
                  <p className="text-sm text-gray-500">Assigned Team</p>
                  <p className="font-medium text-gray-900">{ticket.team_name}</p>
                </div>
              )}

              {ticket.assigned_employee_name && (
                <div>
                  <p className="text-sm text-gray-500">Assigned Employee</p>
                  <p className="font-medium text-gray-900">{ticket.assigned_employee_name}</p>
                </div>
              )}
            </div>
          </div>

          {/* Status Actions */}
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Actions</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Update Status
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {['Open', 'In Progress', 'In Testing', 'Resolved', 'Published (Prod)', 'Closed'].map(status => (
                    <button
                      key={status}
                      onClick={() => updateTicketStatus(status)}
                      disabled={updating || ticket.current_status === status}
                      className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                        ticket.current_status === status
                          ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                      }`}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assign to Team
                </label>
                <select
                  onChange={(e) => e.target.value && updateTicketTeam(e.target.value)}
                  disabled={updating}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={ticket.team_assigned || ""}
                >
                  <option value="">Select team...</option>
                  {teams.map(team => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assign to Employee
                </label>
                <select
                  onChange={(e) => e.target.value && assignToEmployee(e.target.value)}
                  disabled={updating}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={ticket.assigned_employee_id || ""}
                >
                  <option value="">Select employee...</option>
                  {employees
                    .filter(employee => !ticket.team_assigned || employee.team_type_id === ticket.team_assigned)
                    .map(employee => (
                      <option key={employee.id} value={employee.id}>
                        {employee.name}
                      </option>
                    ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportTicketDetailEnhanced;